import api from './api';
import { Customer } from './customers';
import { Team } from './teams';
import { Quotation } from './quotations';
import { Staff } from './staffs';

export interface Invoice {
  id: number;
  invoice_number: string;
  date: string;
  quotation_id?: number;
  customer_id?: number;
  agent_id?: number;
  team_id?: number;
  staff_id?: number;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  // Legacy fields for backward compatibility
  issue_date?: string;
  due_date?: string;
  subtotal?: number;
  tax_amount?: number;
  total_amount?: number;
  status?: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
  // Relations
  quotation?: Quotation;
  customer?: Customer;
  agent?: Staff;
  team?: Team;
  staff?: Staff;
  payments?: InvoicePayment[];
  items?: InvoiceItem[];
}

export interface CreateInvoiceData {
  invoice_number: string;
  date: string;
  quotation_id?: number;
  customer_id?: number;
  agent_id?: number;
  team_id?: number;
  staff_id?: number;
  enabled?: boolean;
  // Legacy fields
  issue_date?: string;
  due_date?: string;
  subtotal?: number;
  tax_amount?: number;
  total_amount?: number;
  status?: 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
  notes?: string;
}

export interface InvoiceItem {
  id: number;
  invoice_id: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  unit?: number | string;
  price_per_unit?: number | string;
  discount?: number | string;
  total_price?: number | string;
  sort_order?: number;
  taskType?: { id: number; name: string };
  taskTemplate?: { id: number; name: string };
}

export interface InvoicePayment {
  id: number | string; // Allow string for temporary IDs
  invoice_id: number;
  type: 'official_receipt' | 'credit_note';
  number?: string;
  payment_date: string;
  payment_amount: number;
  enabled: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
}

export interface CreateInvoicePaymentData {
  invoice_id: number;
  type: 'official_receipt' | 'credit_note';
  number?: string;
  payment_date: string;
  payment_amount: number;
  enabled?: boolean;
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const invoiceService = {
  async getAll(params?: { page?: number; per_page?: number; search?: string }) {
    const response = await api.get('/invoices', { params });
    return response.data;
  },

  async getById(id: number): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`);
    return unwrap<Invoice>(response);
  },

  async create(data: CreateInvoiceData): Promise<Invoice> {
    const response = await api.post('/invoices', data);
    return unwrap<Invoice>(response);
  },

  async update(id: number, data: Partial<CreateInvoiceData>): Promise<Invoice> {
    const response = await api.put(`/invoices/${id}`, data);
    return unwrap<Invoice>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/invoices/${id}`);
  },

  async generatePDF(id: number): Promise<{ pdf_url: string }> {
    const response = await api.get(`/invoices/${id}/pdf`);
    return response.data;
  },

  async sendInvoice(id: number, email: string): Promise<void> {
    await api.post(`/invoices/${id}/send`, { email });
  },

  async getNextNumber(date?: string): Promise<string> {
    const response = await api.get('/invoices/next-number', {
      params: date ? { date } : {},
    });
    return response.data?.data?.invoice_number || response.data?.invoice_number;
  },
};

export const invoicePaymentService = {
  async getAll(invoiceId?: number) {
    const response = await api.get('/invoice-payments', {
      params: invoiceId ? { invoice_id: invoiceId } : {},
    });
    return response.data;
  },

  async getById(id: number): Promise<InvoicePayment> {
    const response = await api.get(`/invoice-payments/${id}`);
    return unwrap<InvoicePayment>(response);
  },

  async create(data: CreateInvoicePaymentData): Promise<InvoicePayment> {
    const response = await api.post('/invoice-payments', data);
    return unwrap<InvoicePayment>(response);
  },

  async update(id: number, data: Partial<CreateInvoicePaymentData>): Promise<InvoicePayment> {
    const response = await api.put(`/invoice-payments/${id}`, data);
    return unwrap<InvoicePayment>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/invoice-payments/${id}`);
  },
};
